import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Youtube, ChevronDown, ChevronRight, Loader2, Calendar, User,
  BookOpen, FileText, PlayCircle, Filter, X
} from 'lucide-react';
import { api } from '../lib/api';

const STATUS_META = {
  Scheduled: { cls: 'bg-blue-100 text-blue-700' },
  Completed: { cls: 'bg-emerald-100 text-emerald-700' },
  Cancelled: { cls: 'bg-red-100 text-red-700' },
  Draft: { cls: 'bg-slate-100 text-slate-500' },
};

const YouTubeClassesByChannel = () => {
  const [channels, setChannels] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  // Date range filter — default to 30 days back → 30 days forward
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  });
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [chRes, clRes] = await Promise.all([
        api.getYouTubeChannels(),
        api.getClasses({
          class_type: 'youtube',
          date_from: dateFrom,
          date_to: dateTo,
        }),
      ]);
      setChannels(chRes.data || []);
      // Backend returns a raw array for GET /classes
      const list = Array.isArray(clRes.data) ? clRes.data : (clRes.data?.classes || clRes.data?.items || []);
      setClasses(list);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const groups = useMemo(() => {
    const map = new Map();
    // Seed every active channel so it appears even with zero classes
    for (const ch of channels) {
      map.set(ch.id, { id: ch.id, name: ch.name, classes: [] });
    }
    for (const c of classes) {
      const key = c.youtube_channel_id || '__none__';
      const name = c.youtube_channel_name || 'No Channel';
      if (!map.has(key)) map.set(key, { id: key, name, classes: [] });
      map.get(key).classes.push(c);
    }
    const arr = Array.from(map.values());
    // Filter by statusFilter
    if (statusFilter !== 'all') {
      arr.forEach(g => { g.classes = g.classes.filter(c => (c.status || 'Scheduled') === statusFilter); });
    }
    // Sort by class count desc, then by name
    arr.sort((a, b) => (b.classes.length - a.classes.length) || a.name.localeCompare(b.name));
    return arr;
  }, [channels, classes, statusFilter]);

  const totals = useMemo(() => {
    let scheduled = 0, completed = 0, cancelled = 0, pendingMaterials = 0;
    for (const c of classes) {
      const s = c.status || 'Scheduled';
      if (s === 'Completed') completed += 1;
      else if (s === 'Cancelled') cancelled += 1;
      else scheduled += 1;
      if (!c.pdf_attached || (c.dpp_required !== false && !c.dpp_attached)) pendingMaterials += 1;
    }
    return { total: classes.length, scheduled, completed, cancelled, pendingMaterials };
  }, [classes]);

  const toggle = (id) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));
  const expandAll = () => setCollapsed({});
  const collapseAll = () => setCollapsed(Object.fromEntries(groups.map(g => [g.id, true])));

  return (
    <div className="space-y-5" data-testid="youtube-classes-by-channel-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Youtube className="text-red-500" /> YouTube Classes by Channel
          </h1>
          <p className="text-sm text-slate-500 mt-1">See how many classes are scheduled on each channel — click any section to expand.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={expandAll} data-testid="expand-all-btn" className="px-3 py-1.5 text-xs border border-slate-200 rounded hover:bg-slate-50">Expand all</button>
          <button onClick={collapseAll} data-testid="collapse-all-btn" className="px-3 py-1.5 text-xs border border-slate-200 rounded hover:bg-slate-50">Collapse all</button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3">
        <Filter size={14} className="text-slate-400" />

        {/* Quick date presets */}
        <div className="flex flex-wrap items-center gap-1" data-testid="date-presets">
          {(() => {
            const today = new Date();
            const iso = (d) => d.toISOString().split('T')[0];
            const y = new Date(today); y.setDate(y.getDate() - 1);
            const w = new Date(today); w.setDate(w.getDate() - 6);
            const m = new Date(today); m.setDate(m.getDate() - 29);
            const t30 = new Date(today); t30.setDate(t30.getDate() + 30);
            const presets = [
              { key: 'today', label: 'Today', from: iso(today), to: iso(today) },
              { key: 'yesterday', label: 'Yesterday', from: iso(y), to: iso(y) },
              { key: 'last7', label: 'Last 7 days', from: iso(w), to: iso(today) },
              { key: 'last30', label: 'Last 30 days', from: iso(m), to: iso(today) },
              { key: 'next30', label: 'Next 30 days', from: iso(today), to: iso(t30) },
            ];
            return presets.map(p => {
              const active = dateFrom === p.from && dateTo === p.to;
              return (
                <button
                  key={p.key}
                  onClick={() => { setDateFrom(p.from); setDateTo(p.to); }}
                  data-testid={`preset-${p.key}`}
                  className={`px-2.5 py-1 text-xs font-medium rounded border transition ${
                    active
                      ? 'bg-red-600 text-white border-red-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-red-400 hover:text-red-600'
                  }`}
                >
                  {p.label}
                </button>
              );
            });
          })()}
        </div>

        <div className="w-px h-5 bg-slate-200" />

        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-slate-600">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-2 py-1 text-sm border border-slate-200 rounded"
            data-testid="filter-date-from"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-slate-600">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-2 py-1 text-sm border border-slate-200 rounded"
            data-testid="filter-date-to"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-2 py-1 text-sm border border-slate-200 rounded"
          data-testid="filter-status"
        >
          <option value="all">All statuses</option>
          <option value="Scheduled">Scheduled</option>
          <option value="Completed">Completed</option>
          <option value="Cancelled">Cancelled</option>
        </select>
        {(statusFilter !== 'all') && (
          <button onClick={() => setStatusFilter('all')} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
            <X size={12} /> Clear
          </button>
        )}
      </div>

      {/* Global totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total classes" value={totals.total} color="slate" />
        <StatCard label="Scheduled" value={totals.scheduled} color="blue" />
        <StatCard label="Completed" value={totals.completed} color="emerald" />
        <StatCard label="Pending materials" value={totals.pendingMaterials} color={totals.pendingMaterials ? 'orange' : 'slate'} />
      </div>

      {/* Channel Accordion */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>
      ) : groups.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-lg text-slate-500">
          <PlayCircle size={40} className="mx-auto text-slate-300 mb-3" />
          <p>No YouTube channels or classes in this date range.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="channels-accordion">
          {groups.map(g => (
            <ChannelSection
              key={g.id}
              group={g}
              collapsed={collapsed[g.id] !== undefined ? collapsed[g.id] : (g.classes.length === 0)}
              onToggle={() => toggle(g.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color }) => {
  const colors = {
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
  };
  return (
    <div className={`border rounded-lg px-3 py-3 ${colors[color]}`}>
      <div className="text-xs font-medium uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
    </div>
  );
};

const ChannelSection = ({ group, collapsed, onToggle }) => {
  const counts = useMemo(() => {
    let scheduled = 0, completed = 0, cancelled = 0, pendingMat = 0;
    for (const c of group.classes) {
      const s = c.status || 'Scheduled';
      if (s === 'Completed') completed += 1;
      else if (s === 'Cancelled') cancelled += 1;
      else scheduled += 1;
      if (!c.pdf_attached || (c.dpp_required !== false && !c.dpp_attached)) pendingMat += 1;
    }
    return { scheduled, completed, cancelled, pendingMat };
  }, [group.classes]);

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`channel-section-${group.id}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition"
        data-testid={`channel-toggle-${group.id}`}
      >
        <div className="flex items-center gap-2 flex-1 text-left">
          {collapsed ? <ChevronRight size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-500" />}
          <Youtube size={16} className="text-red-500" />
          <span className="font-semibold text-slate-800">{group.name}</span>
          <span className="text-sm text-slate-500 ml-2">·  <b className="text-slate-800">{group.classes.length}</b> class{group.classes.length === 1 ? '' : 'es'}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          {counts.scheduled > 0 && <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold">{counts.scheduled} Scheduled</span>}
          {counts.completed > 0 && <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">{counts.completed} Done</span>}
          {counts.cancelled > 0 && <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 font-semibold">{counts.cancelled} Cancelled</span>}
          {counts.pendingMat > 0 && <span className="px-2 py-0.5 rounded bg-orange-100 text-orange-700 font-semibold">{counts.pendingMat} Pending mat.</span>}
        </div>
      </button>
      {!collapsed && (
        group.classes.length === 0 ? (
          <p className="text-sm text-slate-400 italic px-4 py-6 text-center">No classes for this channel in the filter range.</p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid={`channel-classes-${group.id}`}>
            {group.classes
              .slice()
              .sort((a, b) => (a.scheduled_date || '').localeCompare(b.scheduled_date || '') || (a.scheduled_time || '').localeCompare(b.scheduled_time || ''))
              .map(c => {
                const status = c.status || 'Scheduled';
                const statusMeta = STATUS_META[status] || STATUS_META.Scheduled;
                return (
                  <li key={c.id} className="px-4 py-2.5 hover:bg-slate-50 flex items-center gap-3" data-testid={`class-row-${c.id}`}>
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${statusMeta.cls}`}>
                      {status}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800 truncate">{c.title || c.topic || '(untitled)'}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5 flex-wrap">
                        <span className="inline-flex items-center gap-1"><Calendar size={11} /> {c.scheduled_date || '—'}{c.scheduled_time ? ` · ${c.scheduled_time}` : ''}</span>
                        {c.teacher_name && <span className="inline-flex items-center gap-1"><User size={11} /> {c.teacher_name}</span>}
                        {c.subject && <span className="inline-flex items-center gap-1"><BookOpen size={11} /> {c.subject}</span>}
                        {c.pdf_attached && <span className="inline-flex items-center gap-1 text-blue-600"><FileText size={11} /> PDF</span>}
                        {c.dpp_attached && <span className="inline-flex items-center gap-1 text-emerald-600">DPP ✓</span>}
                        {!c.dpp_attached && c.dpp_required === false && <span className="inline-flex items-center gap-1 text-slate-400">DPP N/R</span>}
                      </div>
                    </div>
                  </li>
                );
              })}
          </ul>
        )
      )}
    </div>
  );
};

export default YouTubeClassesByChannel;
